'use client';

import { useState, useRef, useEffect } from 'react';
import type { RawIdea } from '@/lib/foundry/types';

interface BrainDumpProps {
  ideas: RawIdea[];
  onIdeasChange: (ideas: RawIdea[]) => void;
  onOrganize: () => void;
  isOrganizing?: boolean;
}

const PLACEHOLDER_PROMPTS = [
  "What problem keeps you up at night?",
  "Who would pay for this?",
  "What's your unfair advantage?",
  "How is this better than alternatives?",
  "What would the MVP look like?",
  "How would people find out about this?",
];

export function BrainDump({ ideas, onIdeasChange, onOrganize, isOrganizing }: BrainDumpProps) {
  const [currentInput, setCurrentInput] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Rotate placeholder prompts
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_PROMPTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const addIdea = () => {
    const content = currentInput.trim();
    if (!content) return;

    const newIdea: RawIdea = {
      id: crypto.randomUUID(),
      content,
      created_at: new Date().toISOString(),
    };

    onIdeasChange([...ideas, newIdea]);
    setCurrentInput('');
    inputRef.current?.focus();
  };

  const removeIdea = (id: string) => {
    onIdeasChange(ideas.filter((idea) => idea.id !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addIdea();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text');
    const lines = text.split('\n').filter((line) => line.trim());

    if (lines.length > 1) {
      e.preventDefault();
      const newIdeas: RawIdea[] = lines.map((line) => ({
        id: crypto.randomUUID(),
        content: line.trim(),
        created_at: new Date().toISOString(),
      }));
      onIdeasChange([...ideas, ...newIdeas]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-2">Brain Dump</h2>
        <p className="text-foreground-tertiary">
          Throw in all your ideas, fragments, and half-formed thoughts. No structure needed.
        </p>
      </div>

      {/* Input Area */}
      <div className="relative">
        <textarea
          ref={inputRef}
          value={currentInput}
          onChange={(e) => setCurrentInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={PLACEHOLDER_PROMPTS[placeholderIndex]}
          className="w-full h-32 px-4 py-3 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground/30 focus:outline-none focus:border-accent resize-none"
          disabled={isOrganizing}
        />
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          <span className="text-xs text-foreground-muted">Enter to add</span>
          <button
            onClick={addIdea}
            disabled={!currentInput.trim() || isOrganizing}
            className="px-3 py-1 bg-accent text-accent-foreground text-sm font-semibold rounded hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Add
          </button>
        </div>
      </div>

      {/* Tips */}
      <div className="p-4 bg-background/40 border border-border-muted rounded-lg">
        <p className="text-sm text-foreground-tertiary">
          <span className="text-accent font-semibold">Tips:</span> Paste multiple lines to add them all at once.
          Don&apos;t worry about organization - the AI will help structure everything.
        </p>
      </div>

      {/* Ideas List */}
      {ideas.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">
              Your Ideas ({ideas.length})
            </h3>
            {ideas.length >= 3 && (
              <button
                onClick={onOrganize}
                disabled={isOrganizing}
                className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isOrganizing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-navy border-t-transparent"></div>
                    Organizing...
                  </>
                ) : (
                  <>
                    Organize Ideas
                    <span className="text-xs opacity-70">AI</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div className="grid gap-2">
            {ideas.map((idea, index) => (
              <div
                key={idea.id}
                className="group flex items-start gap-3 p-3 bg-background/40 border border-border-muted rounded-lg hover:border-border transition-colors"
              >
                <span className="text-accent font-mono text-sm mt-0.5">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="flex-1 text-foreground text-sm">{idea.content}</p>
                <button
                  onClick={() => removeIdea(idea.id)}
                  className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                  title="Remove idea"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {ideas.length < 3 && (
            <p className="text-sm text-foreground-muted text-center">
              Add at least 3 ideas to enable AI organization
            </p>
          )}
        </div>
      )}

      {/* Empty State */}
      {ideas.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <div className="text-4xl mb-4">💡</div>
          <p className="text-foreground-tertiary mb-2">No ideas yet</p>
          <p className="text-sm text-foreground-muted">
            Start typing above to capture your thoughts
          </p>
        </div>
      )}
    </div>
  );
}
